import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { CreateModuleComponent } from './create-module/create-module.component';
import {
  FirmwareService,
  Module,
  ModuleName,
} from './services/firmware/firmware.service';

@Component({
  selector: 'dcc-modules',
  templateUrl: './modules.component.html',
  styleUrls: ['./modules.component.scss'],
})
export class ModulesComponent implements OnInit {
  numberOfModules = 0;
  pageSize = 50;
  pageSizes: number[] = [50, 100, 150, 200];
  numberOfPages = 0;
  currentPage = 1;
  endOfSet: boolean;
  modules: Module[];
  moduleNames: ModuleName[];
  filteredModuleNames: ModuleName[];
  CORE_FIRMWARE = 'Core Firmware';

  constructor(
    private firmwareService: FirmwareService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.onLoad();
  }

  async onLoad() {
    await this.getData();
  }

  goToModuleDetails(module: any) {
    if (module.name == this.CORE_FIRMWARE) {
      this.router.navigate(['/modules/firmware']);
    } else {
      this.router.navigate(['/modules', module.name], { state: module });
    }
  }

  async getData(): Promise<void> {
    try {
      this.moduleNames = await this.firmwareService.getModuleNames();
      this.moduleNames.sort((a, b) => {
        if (a.name === this.CORE_FIRMWARE) {
          return -1; // 'Core Firmware' should be the first module
        }
        if (b.name === this.CORE_FIRMWARE) {
          return 1; // 'Core Firmware' should be the first module
        }
        return a.name.localeCompare(b.name); // sort by name in ascending order for other lists of modules
      });
      this.numberOfModules = this.moduleNames.length;
      this.applyPagination();
    } catch (error) {
      this.toastr.error('Failure retrieving Module data.');
    }
  }

  onModuleCreateClick() {
    const modal = this.modalService.open(CreateModuleComponent, {
      size: 'lg',
      windowClass: 'lytxModal',
      centered: true,
    });
    modal.componentInstance.refreshPage.subscribe(
      async () => await this.refreshAfterNewModule(),
    );
  }

  async refreshAfterNewModule() {
    this.onLoad();
  }

  onPageSizeSelected(pageSize: number): void {
    this.resetPagination();
    this.pageSize = pageSize;
    this.applyPagination();
  }

  onPageSelection(pageNumber: number): void {
    if (this.currentPage === pageNumber) {
      return;
    }
    this.currentPage = pageNumber;
    this.applyPagination();
  }

  private resetPagination(): void {
    this.numberOfPages = 1;
    this.currentPage = 1;
  }

  private applyPagination(): void {
    const totalPages = Math.ceil(this.moduleNames.length / this.pageSize);

    this.numberOfPages = totalPages === 0 ? 1 : totalPages;

    this.currentPage = Math.max(
      1,
      Math.min(this.currentPage, this.numberOfPages),
    );

    const startIndex = (this.currentPage - 1) * this.pageSize;

    const startIdxOffset = startIndex + this.pageSize;

    const endIndex = Math.min(startIdxOffset, this.moduleNames.length);

    const pageData = this.moduleNames.slice(startIndex, endIndex);

    this.endOfSet = this.currentPage === this.numberOfPages ? true : false;

    this.filteredModuleNames = pageData;
  }
}
